import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PublicSchedule from './pages/PublicSchedule';
import ScheduleTemplates from './pages/ScheduleTemplates';
import Upload from './pages/Upload';
import UploadApplicants from './pages/UploadApplicants';
import ApplicantPipeline from './pages/ApplicantPipeline';
import UploadProjections from './pages/UploadProjections';
import ManageUploads from './pages/ManageUploads';
import CredentialUpload from './pages/CredentialUpload';
import UploadDeclinations from './pages/UploadDeclinations';
import UploadInvoices from './pages/UploadInvoices';
import AgencyProfiles from './pages/AgencyProfiles';
import AgencyDetail from './pages/AgencyDetail';
import ReportsPage from './pages/ReportsPage';
import VisitHistory from './pages/VisitHistory';
import AdminConsole from './pages/AdminConsole';
import DeclinationsMap from './pages/DeclinationsMap';
import DeclinationsReview from './pages/DeclinationsReview';
import CredentialsEmailReview from './pages/CredentialsEmailReview';
import NameReview from './pages/NameReview';
import BookingPage from './pages/public/BookingPage';
import ManageBooking from './pages/public/ManageBooking';
import InvoiceVerification from './pages/InvoiceVerification';
import DataCleanup from './pages/DataCleanup';
import PermissionManagementV2 from './pages/PermissionManagementV2';
import UserPermissions from './pages/UserPermissions';
import ExternalIntegrations from './pages/ExternalIntegrations';
import ESignatureManagement from './pages/ESignatureManagement';
import ProspectsPage from './pages/ProspectsPage';
import DynamicPage from './pages/DynamicPage';
import SiteAdmin from './pages/admin/SiteAdmin';
import PublicSigningPage from './pages/PublicSigningPage';
import VisitClassification from './pages/VisitClassification';
// Personnel & ATS Pages
import PersonnelHub from './pages/PersonnelHub';
import DataImport from './pages/DataImport';
import PersonnelList from './pages/PersonnelList';
import PersonnelDetail from './pages/PersonnelDetail';
import JobPostings from './pages/JobPostings';
import JobPostingDetail from './pages/JobPostingDetail';
import Applicants from './pages/Applicants';
import ApplicantDetail from './pages/ApplicantDetail';
import Careers from './pages/Careers';
import PublicLayout from './pages/public/PublicLayout';
import PublicHome from './pages/public/PublicHome';
import PublicCareers from './pages/public/PublicCareers';
import PublicApply from './pages/public/PublicApply';
import PublicProspectForm from './pages/PublicProspectForm';
import Recruiting from './pages/Recruiting';
import Communications from './pages/Communications';
import SchoolDetail from './pages/SchoolDetail';
import JobDuties from './pages/JobDuties';
import SignatureDocuments from './pages/SignatureDocuments';
import Notifications from './pages/Notifications';
import PTOAnalytics from './pages/PTOAnalytics';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import SigningPage from './pages/SigningPage';
import Settings from './pages/Settings';
import TrackingConsole from './pages/TrackingConsole';
import ReportBuilder from './pages/ReportBuilder';
import MyProfile from './pages/MyProfile';
import ActivatePage from './pages/ActivatePage';
import EmployeeHome from './pages/EmployeeHome';
import OnboardingGallery from './pages/OnboardingGallery';
import CredentialTimeline from './pages/CredentialTimeline';
import DocumentTypes from './pages/DocumentTypes';
import UploadHistoricalWeekly from './pages/UploadHistoricalWeekly';
import ScorecardCriteria from './pages/ScorecardCriteria';
import NotificationRules from './pages/NotificationRules';
import AdminDocumentExport from './pages/AdminDocumentExport';
import CareersAdmin from './pages/CareersAdmin';
import InterviewCalendar from './pages/InterviewCalendar';
import CredentialAudit from './pages/CredentialAudit';
import MissingDocuments from './pages/MissingDocuments';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserNamesProvider } from './context/UserNamesContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { ViewAsProvider, useViewAs } from './context/ViewAsContext';
import { TourProvider } from './context/TourContext';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Smart redirect component that finds the first available page for the user
function DeepLinkToLogin() {
  const location = useLocation();
  // Set synchronously (not in an effect) so it's guaranteed to persist before the
  // <Navigate> below unmounts this component and swaps the route to /login.
  try {
    const target = location.pathname + location.search;
    if (target && !target.startsWith('/login')) {
      localStorage.setItem('post_login_redirect', target);
    }
  } catch (e) { /* ignore */ }
  return <Navigate to="/login" replace />;
}

function SmartRedirect() {
  const { user } = useAuth();
  const { isViewingAsOther, effectivePermissions, viewAsLoading, viewingAs } = useViewAs();
  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Capture the deep-link target ONCE during render (before any effect removes it).
  // A ref survives StrictMode's double effect-invocation and re-renders, so the
  // second run can't fall through to the dashboard after localStorage is cleared.
  const savedRedirectRef = useRef(undefined);
  if (savedRedirectRef.current === undefined) {
    try {
      const saved = localStorage.getItem('post_login_redirect');
      savedRedirectRef.current = (saved && !saved.startsWith('/login') && saved !== '/') ? saved : null;
    } catch (e) {
      savedRedirectRef.current = null;
    }
  }

  useEffect(() => {
    const determineRedirect = async () => {
      // If the user was deep-linked to a specific record (e.g. "View Record" in an
      // email) before authenticating, send them there instead of the dashboard.
      if (savedRedirectRef.current) {
        try { localStorage.removeItem('post_login_redirect'); } catch (e) { /* ignore */ }
        setRedirectTo(savedRedirectRef.current);
        setLoading(false);
        return;
      }

      // If viewing as another user, use their permissions directly
      if (isViewingAsOther) {
        // Wait for ViewAs context to load
        if (viewAsLoading) {
          return; // Keep loading until viewAs is ready
        }
        
        const navPerms = effectivePermissions?.nav_permissions || {};
        console.log('SmartRedirect: Using ViewAs permissions:', navPerms);
        if (viewingAs?.role !== 'admin') {
          setRedirectTo('/home');
          setLoading(false);
          return;
        }
        
        // Find first available page based on viewed user's permissions
        if (navPerms.dashboard === true) {
          setRedirectTo('/dashboard');
        } else if (navPerms.reports === true) {
          setRedirectTo('/reports');
        } else if (navPerms.map === true) {
          setRedirectTo('/reports?view=map');
        } else if (navPerms.agencies === true) {
          setRedirectTo('/agencies');
        } else if (navPerms.personnel === true) {
          setRedirectTo('/personnel');
        } else {
          setRedirectTo('/settings');
        }
        setLoading(false);
        return;
      }
      
      // New hires with an incomplete onboarding checklist go straight to their
      // onboarding tab. Admins are never forced here.
      if (user?.role !== 'admin' && user?.personnel_id) {
        try {
          const token = localStorage.getItem('token');
          const ob = await axios.get(
            `${API}/personnel/${user.personnel_id}/onboarding?viewer=self`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (ob.data && ob.data.total_items > 0 && ob.data.status !== 'Complete') {
            setRedirectTo('/my-profile?tab=onboarding');
            setLoading(false);
            return;
          }
        } catch (e) { /* fall through to normal redirect */ }
      }

      // Non-admins only have self-service pages (backend enforces the same).
      if (user?.role !== 'admin') {
        setRedirectTo('/home');
        setLoading(false);
        return;
      }

      // Default to dashboard for admins or users without permission level
      if (user?.role === 'admin' || !user?.permission_level_id) {
        setRedirectTo('/dashboard');
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/permission-levels/${user.permission_level_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const level = response.data;
        const navPerms = level.nav_permissions || {};
        const adminPerms = level.admin_permissions || {};

        // Find first available page based on permissions
        if (navPerms.dashboard === true) {
          setRedirectTo('/dashboard');
        } else if (navPerms.reports === true || navPerms.map === true) {
          setRedirectTo('/reports');
        } else if (navPerms.agencies === true) {
          setRedirectTo('/agencies');
        } else if (navPerms.personnel === true) {
          setRedirectTo('/personnel');
        } else if (Object.values(adminPerms).some(v => v === true)) {
          setRedirectTo('/admin');
        } else {
          // Non-admin with no nav perms → focused Employee Home
          setRedirectTo('/home');
        }
      } catch (error) {
        console.error('Failed to determine redirect:', error);
        setRedirectTo('/dashboard');
      }
      setLoading(false);
    };

    determineRedirect();
  }, [user, isViewingAsOther, effectivePermissions, viewAsLoading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <Navigate to={redirectTo} replace />;
}

function AppRoutes() {
  const { isAuthenticated, loading, logout, user, mustChangePassword, clearMustChangePassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const handleLogin = () => {
    // Auth state is managed by AuthContext - just navigate
    // The isAuthenticated state should already be true from AuthContext
    console.log('handleLogin called, isAuthenticated:', isAuthenticated);
  };

  return (
    <>
      <Routes>
        {/* Public marketing site — only when NOT authenticated.
            Authenticated users see the existing app at "/" instead. */}
        {!isAuthenticated && (
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<DynamicPage />} />
            <Route path="careers" element={<PublicCareers />} />
            <Route path="careers/apply/:jobId" element={<PublicApply />} />
            {/* Catch-all for any CMS page — must come AFTER explicit routes above */}
            <Route path="*" element={<DynamicPage />} />
          </Route>
        )}

        {/* Public prospect form — accessible whether or not user is logged in */}
        <Route path="/recruiting/apply" element={<PublicProspectForm />} />

        {/* Legacy public Careers (older flow) — preserved for backwards compat */}
        <Route path="/legacy-careers" element={<Careers />} />
        <Route path="/sign/:requestId" element={<PublicSigningPage />} />
        <Route path="/schedule/:token" element={<PublicSchedule />} />
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/booking/:token" element={<ManageBooking />} />
        <Route path="/activate" element={<ActivatePage />} />
        
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <SmartRedirect />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <UserNamesProvider>
                <Layout onLogout={logout} />
              </UserNamesProvider>
            ) : (
              <DeepLinkToLogin />
            )
          }
        >
          <Route index element={<SmartRedirect />} />
          <Route path="dashboard" element={
            <ProtectedRoute permission="view_dashboard">
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="settings" element={<Settings />} />
          <Route path="my-profile" element={<MyProfile />} />
          <Route path="home" element={<EmployeeHome />} />
          
          {/* Upload Routes - require specific upload permissions */}
          <Route path="upload" element={
            <ProtectedRoute permission="upload_visits">
              <Upload />
            </ProtectedRoute>
          } />
          <Route path="upload-projections" element={
            <ProtectedRoute permission="upload_projections">
              <UploadProjections />
            </ProtectedRoute>
          } />
          <Route path="manage-uploads" element={
            <ProtectedRoute permission="manage_uploads">
              <ManageUploads />
            </ProtectedRoute>
          } />
          <Route path="credential-upload" element={
            <ProtectedRoute permission="upload_credentials">
              <CredentialUpload />
            </ProtectedRoute>
          } />
          <Route path="upload-declinations" element={
            <ProtectedRoute permission="upload_declinations">
              <UploadDeclinations />
            </ProtectedRoute>
          } />
          <Route path="upload-invoices" element={
            <ProtectedRoute permission="upload_invoices">
              <UploadInvoices />
            </ProtectedRoute>
          } />
          <Route path="upload-applicants" element={
            <ProtectedRoute permission="upload_visits">
              <UploadApplicants />
            </ProtectedRoute>
          } />
          <Route path="upload-historical-weekly" element={
            <ProtectedRoute permission="upload_visits">
              <UploadHistoricalWeekly />
            </ProtectedRoute>
          } />
          <Route path="import" element={
            <ProtectedRoute permission="upload_visits">
              <DataImport />
            </ProtectedRoute>
          } />
          
          {/* Legacy Profile Routes - Redirect to Personnel */}
          <Route path="profiles" element={<Navigate to="/personnel" replace />} />
          <Route path="profiles/:profileId" element={<Navigate to="/personnel" replace />} />
          
          {/* Agencies */}
          <Route path="agencies" element={
            <ProtectedRoute permission="view_agencies">
              <AgencyProfiles />
            </ProtectedRoute>
          } />
          <Route path="agencies/:agencyId" element={
            <ProtectedRoute permission="view_agency_details">
              <AgencyDetail />
            </ProtectedRoute>
          } />
          
          {/* Reports - Combined page with tabs */}
          <Route path="reports" element={
            <ProtectedRoute permission="view_reports">
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="visit-history" element={
            <ProtectedRoute permission="view_reports">
              <VisitHistory />
            </ProtectedRoute>
          } />
          {/* Legacy routes - redirect to combined reports page */}
          <Route path="visits" element={<Navigate to="/reports" replace />} />
          <Route path="map" element={<Navigate to="/reports" replace />} />
          
          {/* Admin Routes */}
          <Route path="admin" element={
            <ProtectedRoute permissions={["view_admin_console", "view_invoice_verification", "view_signature_documents", "manage_users", "view_declinations_admin", "manage_permission_levels"]}>
              <AdminConsole />
            </ProtectedRoute>
          } />
          <Route path="admin/job-duties" element={
            <ProtectedRoute permission="view_admin_console">
              <JobDuties />
            </ProtectedRoute>
          } />
          <Route path="admin/signature-documents" element={<Navigate to="/admin/signatures" replace />} />
          <Route path="admin/esignature" element={<Navigate to="/admin/signatures" replace />} />
          <Route path="admin/signatures" element={
            <ProtectedRoute permission="view_signature_documents">
              <ESignatureManagement />
            </ProtectedRoute>
          } />
          <Route path="notifications" element={
            <ProtectedRoute selfService>
              <Notifications />
            </ProtectedRoute>
          } />
          <Route path="admin/declinations-map" element={
            <ProtectedRoute permission="view_declinations_admin">
              <DeclinationsMap />
            </ProtectedRoute>
          } />
          <Route path="admin/declinations-review" element={
            <ProtectedRoute permission="view_declinations_admin">
              <DeclinationsReview />
            </ProtectedRoute>
          } />
          <Route path="admin/credentials-email-review" element={
            <ProtectedRoute permission="upload_credentials">
              <CredentialsEmailReview />
            </ProtectedRoute>
          } />
          <Route path="admin/invoices" element={
            <ProtectedRoute permission="view_invoice_verification">
              <InvoiceVerification />
            </ProtectedRoute>
          } />
          <Route path="admin/data-cleanup" element={
            <ProtectedRoute permission="view_invoice_verification">
              <DataCleanup />
            </ProtectedRoute>
          } />
          <Route path="admin/permissions" element={
            <ProtectedRoute permission="manage_permission_levels">
              <PermissionManagementV2 />
            </ProtectedRoute>
          } />
          <Route path="admin/user-permissions" element={
            <ProtectedRoute permission="manage_users">
              <UserPermissions />
            </ProtectedRoute>
          } />
          <Route path="admin/integrations" element={
            <ProtectedRoute permission="admin_only">
              <ExternalIntegrations />
            </ProtectedRoute>
          } />
          <Route path="admin/esignature" element={<Navigate to="/admin/signatures" replace />} />
          <Route path="admin/visit-classification" element={
            <ProtectedRoute permission="view_admin_console">
              <VisitClassification />
            </ProtectedRoute>
          } />
          <Route path="admin/tracking" element={
            <ProtectedRoute permission="view_admin_console">
              <TrackingConsole />
            </ProtectedRoute>
          } />
          <Route path="admin/report-builder" element={
            <ProtectedRoute permission="view_admin_console">
              <ReportBuilder />
            </ProtectedRoute>
          } />
          <Route path="admin/onboarding-gallery" element={
            <ProtectedRoute permission="view_admin_console">
              <OnboardingGallery />
            </ProtectedRoute>
          } />
          <Route path="admin/credential-timeline" element={
            <ProtectedRoute permission="view_admin_console">
              <CredentialTimeline />
            </ProtectedRoute>
          } />
          <Route path="admin/pto-analytics" element={
            <ProtectedRoute permission="view_admin_console">
              <PTOAnalytics />
            </ProtectedRoute>
          } />
          <Route path="admin/analytics" element={
            <ProtectedRoute permission="view_admin_console">
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />
          <Route path="admin/applicant-pipeline" element={
            <ProtectedRoute permission="view_admin_console">
              <ApplicantPipeline />
            </ProtectedRoute>
          } />
          <Route path="admin/document-types" element={
            <ProtectedRoute permission="view_admin_console">
              <DocumentTypes />
            </ProtectedRoute>
          } />
          <Route path="admin/scorecard-criteria" element={
            <ProtectedRoute permission="view_admin_console">
              <ScorecardCriteria />
            </ProtectedRoute>
          } />
          <Route path="admin/notification-rules" element={
            <ProtectedRoute permission="view_admin_console">
              <NotificationRules />
            </ProtectedRoute>
          } />
          <Route path="admin/document-export" element={
            <ProtectedRoute permission="view_admin_console">
              <AdminDocumentExport />
            </ProtectedRoute>
          } />
          <Route path="admin/careers" element={
            <ProtectedRoute permission="view_admin_console">
              <CareersAdmin />
            </ProtectedRoute>
          } />
          <Route path="admin/interview-calendar" element={
            <ProtectedRoute permission="view_admin_console">
              <InterviewCalendar />
            </ProtectedRoute>
          } />
          <Route path="admin/schedule-templates" element={
            <ProtectedRoute permission="view_admin_console">
              <ScheduleTemplates />
            </ProtectedRoute>
          } />
          <Route path="admin/name-review" element={
            <ProtectedRoute adminOnly>
              <NameReview />
            </ProtectedRoute>
          } />
          <Route path="admin/credential-audit" element={
            <ProtectedRoute permission="view_admin_console">
              <CredentialAudit />
            </ProtectedRoute>
          } />
          <Route path="admin/missing-documents" element={
            <ProtectedRoute permission="view_admin_console">
              <MissingDocuments />
            </ProtectedRoute>
          } />

          {/* Recruiting & Marketing */}
          <Route path="recruiting" element={
            <ProtectedRoute permission="view_personnel">
              <Recruiting />
            </ProtectedRoute>
          } />
          <Route path="recruiting/prospects" element={
            <ProtectedRoute permission="view_personnel">
              <ProspectsPage />
            </ProtectedRoute>
          } />
          <Route path="recruiting/schools/:schoolId" element={
            <ProtectedRoute permission="view_personnel">
              <SchoolDetail />
            </ProtectedRoute>
          } />

          {/* Public Site / CMS admin */}
          <Route path="admin/site" element={
            <ProtectedRoute permission="view_admin_console">
              <SiteAdmin />
            </ProtectedRoute>
          } />
          <Route path="admin/site/pages/:pageId" element={
            <ProtectedRoute permission="view_admin_console">
              <SiteAdmin />
            </ProtectedRoute>
          } />

          {/* Communications (Phase 2 placeholder - inbox UI coming in later phases) */}
          <Route path="communications" element={
            <ProtectedRoute>
              <Communications />
            </ProtectedRoute>
          } />
          
          {/* Personnel Hub - require personnel permissions */}
          <Route path="personnel" element={
            <ProtectedRoute permission="view_personnel">
              <PersonnelHub />
            </ProtectedRoute>
          } />
          <Route path="personnel/employees" element={
            <ProtectedRoute permission="view_personnel">
              <PersonnelList />
            </ProtectedRoute>
          } />
          <Route path="personnel/contractors" element={
            <ProtectedRoute permission="view_personnel">
              <PersonnelList />
            </ProtectedRoute>
          } />
          <Route path="personnel/applicants" element={
            <ProtectedRoute permission="view_personnel">
              <Applicants />
            </ProtectedRoute>
          } />
          <Route path="personnel/job-postings" element={
            <ProtectedRoute permission="view_personnel">
              <JobPostings />
            </ProtectedRoute>
          } />
          <Route path="personnel/:personnelId" element={
            <ProtectedRoute permission="view_personnel_details">
              <PersonnelDetail />
            </ProtectedRoute>
          } />
          
          {/* Job Posting & Applicant Detail Routes */}
          <Route path="ats/postings/:postingId" element={
            <ProtectedRoute permission="view_personnel">
              <JobPostingDetail />
            </ProtectedRoute>
          } />
          <Route path="ats/applicants/:applicantId" element={
            <ProtectedRoute permission="view_personnel">
              <ApplicantDetail />
            </ProtectedRoute>
          } />
        </Route>
        {/* Public signing page - no auth required */}
        <Route path="/sign/:token" element={<SigningPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SessionTimeoutWarning />
      {/* Force password change modal - blocks everything until password is changed */}
      {isAuthenticated && mustChangePassword && (
        <ForcePasswordChangeModal 
          user={user} 
          onPasswordChanged={clearMustChangePassword} 
        />
      )}
    </>
  );
}

function App() {
  useEffect(() => {
    document.title = 'Elite Home Rehab';
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PermissionsProvider>
            <ViewAsProvider>
              <TourProvider>
                <AppRoutes />
              </TourProvider>
            </ViewAsProvider>
          </PermissionsProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
